import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollToTop } from './components/ScrollToTop';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { BrandingProvider } from './context/BrandingContext';
import { LanguagesProvider } from './context/LanguagesContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AuthCallback } from './pages/AuthCallback';
import { BrowsePage } from './pages/BrowsePage';
import { ListingDetailPage } from './pages/ListingDetailPage';
import { CreateListingPage } from './pages/CreateListingPage';
import { MyListingsPage } from './pages/MyListingsPage';
import { MessagesPage } from './pages/MessagesPage';
import { MessageThreadPage } from './pages/MessageThreadPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { AdminPage } from './pages/AdminPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { VerificationRequiredPage } from './pages/VerificationRequiredPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { ProfileSettingsPage } from './pages/ProfileSettingsPage';
import { DynamicContentPage } from './pages/DynamicContentPage';
import { ContentPage } from './pages/ContentPage';
import { SavedSearchesPage } from './pages/SavedSearchesPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { Toaster } from './components/ui/sonner';
import { CurrencySuggestion } from './components/CurrencySuggestion';
import { MaintenanceGate } from './components/MaintenanceGate';
import './App.css';

function AppRouter() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Sync the browser tab title with the active i18n language. Re-runs on
  // every `language` change so switching FR / EN / MG updates the title
  // instantly without a reload.
  useEffect(() => {
    document.title = t('site.title');
  }, [t, i18n.language]);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verification-requise" element={<VerificationRequiredPage />} />
      <Route path="/browse" element={<BrowsePage />} />
      <Route path="/listings/:id" element={<ListingDetailPage />} />
      <Route path="/users/:userId" element={<UserProfilePage />} />
      <Route path="/settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Navigate to="/" replace /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
      <Route path="/edit/:id" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
      <Route path="/my-listings" element={<ProtectedRoute><MyListingsPage /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/messages/:listingId/:userId" element={<ProtectedRoute><MessageThreadPage /></ProtectedRoute>} />
      <Route path="/saved-searches" element={<ProtectedRoute><SavedSearchesPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      {/* Legacy routes — redirect to new slug-based pages system */}
      <Route path="/how-it-works" element={<Navigate to="/pages/how-it-works" replace />} />
      <Route path="/promoted-listings" element={<Navigate to="/pages/promoted-listings" replace />} />
      <Route path="/mobile-apps" element={<Navigate to="/pages/mobile-apps" replace />} />
      <Route path="/help-contact" element={<Navigate to="/pages/help-contact" replace />} />
      <Route path="/safety-tips" element={<Navigate to="/pages/safety-tips" replace />} />
      <Route path="/terms-of-use" element={<Navigate to="/pages/terms-of-use" replace />} />
      <Route path="/privacy-policy" element={<Navigate to="/pages/privacy-policy" replace />} />
      {/* New DB-driven slug-based pages */}
      <Route path="/pages/:slug" element={<ContentPage />} />
      <Route path="/p/:linkId" element={<DynamicContentPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <LanguagesProvider>
          <AuthProvider>
            <CurrencyProvider>
              <div className="App">
                <MaintenanceGate>
                  <AppRouter />
                </MaintenanceGate>
                <CurrencySuggestion />
                <Toaster position="bottom-center" duration={2000} />
              </div>
            </CurrencyProvider>
          </AuthProvider>
        </LanguagesProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;